/**
 * 收藏组件（SKC = 商品 + 颜色，不含尺码）。
 *
 * 换店复用：本文件不写死店铺。配置来自 #wishlist-config（snippets/wishlist-config.liquid）。
 * 商品卡色圈只要带 data-wishlist-color / data-wishlist-variant-id / data-wishlist-image，
 * 心形就收藏当前颜色。详情页按钮带 data-color-option-index，变体切换时更新颜色。
 *
 * 存储（店铺名来自配置 store，用来隔开不同店铺）：
 * - 未登录：localStorage `hyt-wishlist:{store}:guest`，session id 在 `hyt-wishlist:{store}:guest-session`
 * - 已登录：localStorage `hyt-wishlist:{store}:c:{customerId}`，并与游客列表合并
 * - 配置了 proxyPath：已登录写入该客户 metafield；未登录按 session id 写入 GuestWishlist
 * - 第一次收藏提示：内存里的列表还是空的，且本标签页 sessionStorage
 *   `first_favorite_notification_shown:{store}` 不是 "true"。弹出后写成 "true"。
 *   同一标签页清空再收藏不再提示；关掉标签页后标记消失。
 * - 未登录最多 guestLimit 条（默认 6）。第 6 个正常写入；再点第 7 个才弹出登录框，并且不写入。
 *   被拦下的那一条存进本店 localStorage `hyt-wishlist:{store}:pending`（只存 key）。
 *   登录/注册后页面刷新，boot 里 applyPendingItem 把它补进该客户名下并清掉标记，
 *   所以用户点了登录不是白点，那一件还在。之后正常收藏成功会撤销这个标记。
 *   登录按钮直接调用 shopify-account.showModal()，不点页头或侧边栏按钮。
 *   宿主不可见时 Shopify 会马上关掉弹框，手机上先让页头账号通过可见性检查再打开。
 *   登录或注册后页面回来，游客列表并入该客户；已登录不走这条上限。
 *   未登录点分享同样打开这个登录框前的提示；Reorder 只给已登录。
 *   已登录点分享打开复制链接弹框。商品少于 3 个全部显示；4 个及以上只露前 3 张，最后一格是 +(总数-3) items。
 *   复制出去的地址是 /pages/wishlist/{code}。code 是大约 20 位的短码，列表存在 App Proxy。
 *   旧的 s1 长码仍能在本地解开。别人打开短码或长码都能看到当时那份收藏。
 *
 * 第三步 App Proxy 约定（Shopify 注入 logged_in_customer_id，不要信请求体里的客户 ID）：
 *   GET  {proxyPath}                              已登录
 *   GET  {proxyPath}?session_id=                  未登录，读 GuestWishlist
 *     200 { "version": 1, "items": Key[] }
 *   POST {proxyPath}
 *     body { "version": 1, "items": { key, productId, handle, color, addedAt }[] }
 *     未登录还要带 sessionId。本次点击带 change: { action: "add"|"remove", key }，
 *     服务端第一次见到这个 session 时只把这一条记进 WishlistEvent。
 *     登录合并游客列表时带 adoptedKeys，这些 key 不再记一条顾客 add。
 *     只发 key 字符串时，服务端会把整份列表存成空的，清掉本地后再刷新就拉不回来。
 *     200 { "version": 1, "items": Key[] }  服务端保存后的完整列表
 *   非 JSON 或非 2xx：组件保持本地列表，不打断浏览。
 *   旧接口若仍返回带 key 的对象，读取时只取 key。
 *
 * 本地只存 key 字符串，顺序就是列表顺序（新的在前）。
 *   key = `${productId}::${colorHandle}`，colorHandle 由颜色名生成，同一颜色只保留一条。
 * 列表页用商品 ID 查出 handle，再按 colorHandle 对上颜色、图片和价格。
 */
(function () {
  var STORAGE_PREFIX = 'hyt-wishlist:';
  var CHANGE_EVENT = 'wishlist:change';
  var MIN_LOADING_MS = 500;
  var REQUEST_TIMEOUT_MS = 8000;
  // 与 wishlist-app 的 readSessionId 一致：8–128 位，不能是纯数字，避免和顾客 id 撞列。
  var SESSION_ID = /^(?=.*[A-Za-z._:-])[A-Za-z0-9._:-]{8,128}$/;

  var config = readConfig();
  var items = [];
  // 登录时从游客列表并入的 key。下一次写入账号时带给服务端，避免再记一条顾客 add。
  var adoptedGuestKeys = [];
  var guestSessionMemo = '';
  // 打开 /pages/wishlist/{code} 时，页面画这份分享，不覆盖访客自己的收藏。
  var sharedList = null;
  var shareLoading = false;
  var reorderMode = false;
  var byKey = {};
  var chain = Promise.resolve();
  var productCache = {};
  var renderToken = 0;
  var activeTip = null;
  var tipTimer = 0;
  var TIP_VISIBLE_MS = 5000;
  var limitModal = null;
  var bodyOverflow = '';

  // 读 #wishlist-config。缺配置时仍可用，只是没有账号同步。
  function readConfig() {
    var node = document.getElementById('wishlist-config');
    var data = {};
    if (node) {
      try {
        data = JSON.parse(node.textContent || '{}') || {};
      } catch (e) {
        data = {};
      }
    }
    data.labels = data.labels || {};
    data.icons = data.icons || {};
    data.maxItems = data.maxItems || 200;
    data.guestLimit = Number(data.guestLimit) > 0 ? Number(data.guestLimit) : 6;
    // 新客户账户登录后，Liquid 的 customer 可能还是空的。页头组件带的 JSON 里有客户 ID。
    if (!data.customerId) data.customerId = customerIdFromAccount();
    return data;
  }

  // gid://shopify/Customer/123 或纯数字都收成客户 ID。没有登录时是空字符串。
  function customerIdFromAccount() {
    var node = document.getElementById('shopify-customer-account-data');
    if (!node) return '';
    try {
      var data = JSON.parse(node.textContent || '{}');
      var id = data && data.customer && data.customer.id;
      if (id == null || id === '') return '';
      var match = String(id).match(/(\d+)\s*$/);
      return match ? match[1] : '';
    } catch (e) {
      return '';
    }
  }

  // 直接打开登录弹框，不查找、不点击页头或侧边栏的账号按钮。
  // Shopify 会在宿主 checkVisibility 失败时关掉弹框。手机上页头账号是 display:none，
  // 先收成 1px 仍保持可见，弹框才能留在屏幕上。关掉后再还原。
  function openAccount() {
    var account = accountForLogin();
    if (!account || typeof account.showModal !== 'function') {
      window.location.href = config.accountUrl || config.loginUrl || '/account/login';
      return;
    }
    ensureAccountVisible(account);
    account.showModal();
  }

  // 桌面用已经看得见的账号，弹框会贴在那个按钮旁边。手机上没有看得见的，用第一个。
  function accountForLogin() {
    var nodes = document.querySelectorAll('shopify-account');
    var fallback = null;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (typeof el.showModal !== 'function') continue;
      if (!fallback) fallback = el;
      if (isAccountVisible(el)) return el;
    }
    return fallback;
  }

  function isAccountVisible(el) {
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ opacityProperty: true, visibilityProperty: true });
    }
    var style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  function ensureAccountVisible(account) {
    if (isAccountVisible(account) || account.getAttribute('data-wishlist-login-host') === '1') return;
    var previous = account.getAttribute('style');
    account.setAttribute('data-wishlist-login-host', '1');
    account.style.setProperty('display', 'block', 'important');
    account.style.setProperty('visibility', 'visible', 'important');
    account.style.setProperty('opacity', '1', 'important');
    account.style.setProperty('position', 'fixed', 'important');
    account.style.setProperty('inset', '0 auto auto 0', 'important');
    account.style.setProperty('width', '1px', 'important');
    account.style.setProperty('height', '1px', 'important');
    account.style.setProperty('overflow', 'hidden', 'important');
    account.style.setProperty('pointer-events', 'none', 'important');
    var restore = function () {
      account.removeAttribute('data-wishlist-login-host');
      if (previous == null) account.removeAttribute('style');
      else account.setAttribute('style', previous);
    };
    var pending = account.showModal();
    if (!pending || typeof pending.then !== 'function') return;
    pending.then(function () {
      var dialog = account.shadowRoot && account.shadowRoot.querySelector('dialog');
      if (!dialog) {
        restore();
        return;
      }
      if (!dialog.open) {
        restore();
        return;
      }
      dialog.addEventListener('close', restore, { once: true });
    }).catch(restore);
  }

  // 店铺名放进 key，同一浏览器里不同店铺的收藏不会混在一起。
  function storeSlug() {
    return handleize(config.store || 'store');
  }

  // 未登录用的 localStorage key。
  function guestKey() {
    return STORAGE_PREFIX + storeSlug() + ':guest';
  }

  // 已登录写到该客户名下，未登录仍用游客 key。
  function storageKey() {
    if (config.customerId) return STORAGE_PREFIX + storeSlug() + ':c:' + config.customerId;
    return guestKey();
  }

  // 游客会话存在本店 localStorage。同一浏览器刷新后还是这个 id，服务端按它找回列表。
  function guestSessionKey() {
    return STORAGE_PREFIX + storeSlug() + ':guest-session';
  }

  // 生成符合服务端 SESSION_ID 的 id。带字母前缀，避免随机结果变成纯数字。
  function createGuestSessionId() {
    var uuid = '';
    try {
      if (window.crypto && typeof crypto.randomUUID === 'function') uuid = crypto.randomUUID();
    } catch (e) { }
    if (!uuid) uuid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    var id = ('g.' + uuid).replace(/[^A-Za-z0-9._:-]/g, '').slice(0, 128);
    if (!SESSION_ID.test(id)) {
      id = ('guest.' + Date.now().toString(36) + Math.random().toString(36).slice(2)).slice(0, 128);
    }
    return id;
  }

  // 未登录才需要。已有合法 id 就复用，没有就生成并写回 localStorage。
  function ensureGuestSessionId() {
    if (config.customerId) return '';
    if (guestSessionMemo && SESSION_ID.test(guestSessionMemo)) return guestSessionMemo;
    var existing = '';
    try { existing = localStorage.getItem(guestSessionKey()) || ''; } catch (e) { }
    existing = String(existing).trim();
    if (!SESSION_ID.test(existing)) existing = createGuestSessionId();
    guestSessionMemo = existing;
    try { localStorage.setItem(guestSessionKey(), existing); } catch (e) { }
    return existing;
  }

  // 旧 key 没有店铺名。新 key 还是空的时候把旧列表搬过来，避免这次改名把已有收藏弄丢。
  function adoptLegacy(nextKey, legacyKey) {
    try {
      if (localStorage.getItem(nextKey) != null) return;
      var legacy = localStorage.getItem(legacyKey);
      if (!legacy) return;
      localStorage.setItem(nextKey, legacy);
      localStorage.removeItem(legacyKey);
    } catch (e) { }
  }

  // 颜色名转成稳定片段。保留中文等字母，避免不同颜色被收成同一个 key。
  function handleize(value) {
    var text = String(value || '').trim().toLowerCase();
    if (!text) return 'default';
    try {
      text = text
        .replace(/\s+/g, '-')
        .replace(/[^\p{L}\p{N}-]+/gu, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    } catch (e) {
      text = text.replace(/\s+/g, '-').replace(/[^a-z0-9-]+/g, '');
    }
    return text || 'default';
  }

  // 字符串、旧对象里的 key，或只有 productId + color 的旧数据，都收成同一条。
  function itemFromStored(entry) {
    var key = '';
    if (typeof entry === 'string') key = entry;
    else if (entry && entry.key) key = String(entry.key);
    else if (entry && entry.productId != null) {
      key = String(entry.productId) + '::' + handleize(entry.color || '');
    }
    var parts = String(key).split('::');
    if (parts.length < 2 || !parts[0] || !parts[1]) return null;
    var colorHandle = parts.slice(1).join('::');
    return {
      key: parts[0] + '::' + colorHandle,
      productId: parts[0],
      colorHandle: colorHandle
    };
  }

  // 只留下 key 字符串，去掉重复，并截到 maxItems。写入本地走这里。
  function storedKeys(list) {
    var keys = [];
    (list || []).forEach(function (item) {
      var key = typeof item === 'string' ? item : item && item.key;
      if (!key || keys.indexOf(key) !== -1) return;
      keys.push(key);
    });
    return keys.slice(0, config.maxItems);
  }

  // 服务端要对象。只有 key 字符串时会被丢弃，整份心愿单写成空列表。
  // handle 本地没有，用商品 ID 占位，避免缺字段被丢掉。addedAt 递减，保住新的在前。
  function remoteItems(list) {
    var now = Date.now();
    var out = [];
    storedKeys(list).forEach(function (key, index) {
      var item = itemFromStored(key);
      if (!item) return;
      out.push({
        key: item.key,
        productId: item.productId,
        handle: item.productId,
        color: item.colorHandle,
        variantId: '',
        image: '',
        url: '',
        title: '',
        addedAt: now - index
      });
    });
    return out;
  }

  // 读出某个存储 key 下的收藏。坏 JSON 当成空列表。
  function readList(key) {
    try {
      var raw = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw.map(itemFromStored).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  // 把列表写成 key 字符串数组。旧的完整对象不会再写回去。
  function writeList(key, list) {
    try {
      localStorage.setItem(key, JSON.stringify(storedKeys(list)));
    } catch (e) { }
  }

  // 先保留 primary 的顺序，再补上 primary 里没有的 secondary。同一 key 只留一条。
  function mergeLists(primary, secondary) {
    var seen = {};
    var out = [];
    function take(item) {
      if (!item || !item.key || seen[item.key]) return;
      seen[item.key] = true;
      out.push(item);
    }
    (primary || []).forEach(take);
    (secondary || []).forEach(take);
    return out.slice(0, config.maxItems);
  }

  // 用 key 顺序做指纹，判断合并后要不要再写回服务端。
  function signature(list) {
    return storedKeys(list).join('|');
  }

  // 用 key 建索引，心形判断已收藏时不用再扫数组。
  function indexItems() {
    byKey = {};
    items.forEach(function (item) {
      byKey[item.key] = item;
    });
  }

  // 被 6 条上限拦下的那一条。存在本店 localStorage，登录/注册完成后补进账号列表。
  function pendingKey() {
    return STORAGE_PREFIX + storeSlug() + ':pending';
  }

  // 只存 key 字符串，和列表同一套格式，登录后 itemFromStored 能还原成一条收藏。
  function readPending() {
    try {
      var raw = localStorage.getItem(pendingKey());
      return raw ? itemFromStored(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writePending(key) {
    try {
      if (key) localStorage.setItem(pendingKey(), String(key));
      else localStorage.removeItem(pendingKey());
    } catch (e) { }
  }

  // 这一条后来正常收藏成功了，就别再当待补的，免得以后登录时多出一样。
  function clearPending(key) {
    var pending = readPending();
    if (pending && pending.key === key) writePending('');
  }

  // 登录/注册回来：把被拦下的那一条补进账号列表。已经收藏过就只清标记。
  function applyPendingItem() {
    if (!config.customerId) return Promise.resolve();
    var pending = readPending();
    if (!pending) return Promise.resolve();
    writePending('');
    if (byKey[pending.key]) return Promise.resolve();
    items = [pending].concat(items);
    if (items.length > config.maxItems) items = items.slice(0, config.maxItems);
    indexItems();
    return persist({ action: 'add', key: pending.key }).then(function () {
      paintAll();
      renderPage();
    });
  }

  // 店铺不在域名根路径时，商品和搜索地址要带上这个前缀。根路径返回空字符串。
  function rootPrefix() {
    var root = config.rootUrl || '/';
    if (!root || root === '/') return '';
    return String(root).replace(/\/$/, '');
  }

  // 商品详情地址。有变体时带上 ?variant=，打开的就是收藏的那一色。
  function productPath(handle, variantId) {
    var path = rootPrefix() + '/products/' + handle;
    if (variantId) path += '?variant=' + variantId;
    return path;
  }

  // 商品卡外壳。Focal、Madrid、Mr Parker 的类名不一样，色圈也可能是单选或链接。
  var CARD_SELECTOR = 'product-item, .product-item, .product-card-wrapper, .card-wrapper, .card-horizontal-wrapper, .product-index, .js-product-listing, .product__section-container';

  function cardScope(node) {
    return (node && node.closest && node.closest(CARD_SELECTOR)) || null;
  }

  function selectedColorControl(scope) {
    if (!scope || !scope.querySelector) return null;
    return scope.querySelector('[data-wishlist-color].js-is-active, [data-wishlist-color].is-selected, [data-wishlist-color].active, [data-wishlist-color]:checked');
  }

  // 详情页的心形不读色圈，先把当前颜色写回按钮再刷新。
  function syncButtonFromControl(control) {
    var card = cardScope(control);
    if (!card || !control) return;
    var button = card.querySelector('[data-wishlist-toggle]');
    if (!button || button.getAttribute('data-wishlist-state') === 'loading') return;
    if (button.getAttribute('data-wishlist-context') === 'product') {
      var color = control.getAttribute('data-wishlist-color');
      var variantId = control.getAttribute('data-wishlist-variant-id');
      var image = control.getAttribute('data-wishlist-image');
      if (color) button.setAttribute('data-color', color);
      if (variantId) button.setAttribute('data-variant-id', variantId);
      if (image) button.setAttribute('data-image', image);
      var handle = button.getAttribute('data-product-handle');
      if (handle && variantId) button.setAttribute('data-url', productPath(handle, variantId));
    }
    paintButton(button);
  }

  // Madrid 的 variant-selects 派发 variant:change，详情里没有 product-media 表单。
  function applyProductVariant(variant, scope) {
    if (!variant) return;
    var root = scope || document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-wishlist-toggle][data-wishlist-context="product"]'), function (button) {
      if (button.closest('[data-wishlist-page]')) return;
      var index = parseInt(button.getAttribute('data-color-option-index'), 10);
      if (index && variant.options && variant.options[index - 1]) {
        button.setAttribute('data-color', variant.options[index - 1]);
      } else if (index === 1 && variant.option1) {
        button.setAttribute('data-color', variant.option1);
      } else if (index === 2 && variant.option2) {
        button.setAttribute('data-color', variant.option2);
      } else if (index === 3 && variant.option3) {
        button.setAttribute('data-color', variant.option3);
      }
      if (variant.id) button.setAttribute('data-variant-id', String(variant.id));
      var image = '';
      if (variant.featured_image && variant.featured_image.src) image = variant.featured_image.src;
      else if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
        image = variant.featured_media.preview_image.src;
      }
      if (image) button.setAttribute('data-image', image);
      var handle = button.getAttribute('data-product-handle');
      if (handle && variant.id) button.setAttribute('data-url', productPath(handle, variant.id));
      if (button.getAttribute('data-wishlist-state') !== 'loading') paintButton(button);
    });
  }

  // 从按钮读出当前 SKC。商品卡优先读选中色圈，详情页读按钮上的颜色。
  function readItem(button) {
    if (!button) return null;
    var productId = button.getAttribute('data-product-id');
    if (!productId) return null;

    var handle = button.getAttribute('data-product-handle') || '';
    var color = button.getAttribute('data-color') || '';
    var variantId = button.getAttribute('data-variant-id') || '';
    var image = button.getAttribute('data-image') || '';

    if (button.getAttribute('data-wishlist-context') !== 'product') {
      var scope = cardScope(button) || button.parentElement;
      var radio = selectedColorControl(scope);
      if (radio) {
        color = radio.getAttribute('data-wishlist-color') || color;
        variantId = radio.getAttribute('data-wishlist-variant-id') || variantId;
        image = radio.getAttribute('data-wishlist-image') || image;
      }
    }

    var colorHandle = handleize(color);
    return {
      key: String(productId) + '::' + colorHandle,
      productId: String(productId),
      colorHandle: colorHandle,
      handle: handle,
      color: color,
      variantId: variantId,
      image: image,
      url: handle ? productPath(handle, variantId) : ''
    };
  }

  // 文案优先用配置，缺了再用英文兜底。
  function label(name, fallback) {
    return (config.labels && config.labels[name]) || fallback;
  }

  // 切换心形的未收藏、已收藏、保存中三种样子，并改无障碍文案。
  function applyState(button, state) {
    button.setAttribute('data-wishlist-state', state);
    button.setAttribute('aria-pressed', state === 'saved' ? 'true' : 'false');
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    if (state === 'loading') {
      button.setAttribute('aria-label', label('loading', 'Saving'));
      button.removeAttribute('data-wishlist-tip');
    } else if (state === 'saved') {
      var savedLabel = label('saved', 'Remove from favorites');
      button.setAttribute('aria-label', savedLabel);
      button.setAttribute('data-wishlist-tip', savedLabel);
    } else {
      var saveLabel = label('save', 'Add to favorites');
      button.setAttribute('aria-label', saveLabel);
      button.setAttribute('data-wishlist-tip', saveLabel);
    }
  }

  // 按当前列表把一颗心画成已收藏或未收藏。
  function paintButton(button) {
    var item = readItem(button);
    applyState(button, item && byKey[item.key] ? 'saved' : 'unsaved');
  }

  // root 省略时刷新整页。loading 中的按钮不被刷新盖掉。
  function paintAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-wishlist-toggle]'), function (button) {
      if (button.getAttribute('data-wishlist-state') === 'loading') return;
      paintButton(button);
    });
    paintCounts();
  }

  // 头部收藏数量。0 时藏起来。
  function paintCounts() {
    var count = items.length;
    Array.prototype.forEach.call(document.querySelectorAll('[data-wishlist-count]'), function (node) {
      node.textContent = String(count);
      if (count > 0) node.removeAttribute('hidden');
      else node.setAttribute('hidden', '');
    });
  }

  // 同一个 SKC 的心形一起转圈，避免连点。
  function markKeyLoading(key) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-wishlist-toggle]'), function (button) {
      var item = readItem(button);
      if (item && item.key === key) applyState(button, 'loading');
    });
  }

  // 保存再快也至少转 MIN_LOADING_MS，避免图标闪一下。
  function waitAtLeast(started) {
    var left = MIN_LOADING_MS - (Date.now() - started);
    if (left <= 0) return Promise.resolve();
    return new Promise(function (resolve) {
      setTimeout(resolve, left);
    });
  }

  // 超时就中断。账号同步失败时不一直挂着。
  function fetchWithTimeout(url, options) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, REQUEST_TIMEOUT_MS);
    var opts = options || {};
    if (controller) opts.signal = controller.signal;
    return fetch(url, opts).then(function (response) {
      clearTimeout(timer);
      return response;
    }, function (error) {
      clearTimeout(timer);
      throw error;
    });
  }

  // 已登录或已有游客 session 才请求。失败（App 还没装、超时、返回 HTML）时保持本地。
  function fetchRemote(method, body) {
    var sessionId = config.customerId ? '' : ensureGuestSessionId();
    if (!config.proxyPath || (!config.customerId && !sessionId)) {
      return Promise.reject(new Error('wishlist local only'));
    }
    var url = config.proxyPath;
    // 游客读列表走 query。写列表时把 sessionId 放进 body。
    if (sessionId && method === 'GET') {
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'session_id=' + encodeURIComponent(sessionId);
    }
    var options = {
      method: method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    };
    if (body) {
      if (sessionId) body.sessionId = sessionId;
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return fetchWithTimeout(url, options).then(function (response) {
      if (!response.ok) throw new Error('wishlist proxy');
      return response.json();
    });
  }

  // 先写本地 key 列表，再交给服务端。游客带 sessionId；本次点击再带 change。失败就留在本地。
  function persist(change) {
    writeList(storageKey(), items);
    if (!config.proxyPath) return Promise.resolve();
    if (!config.customerId && !ensureGuestSessionId()) return Promise.resolve();
    var body = { version: 1, items: remoteItems(items) };
    if (!config.customerId && change && (change.action === 'add' || change.action === 'remove') && change.key) {
      body.change = { action: change.action, key: change.key };
    }
    if (config.customerId && adoptedGuestKeys.length) {
      body.adoptedKeys = adoptedGuestKeys.slice();
    }
    return fetchRemote('POST', body).then(function (data) {
      if (!data || !Array.isArray(data.items)) return;
      var remote = data.items.map(itemFromStored).filter(Boolean);
      // 接口异常返回空列表时，保留本地，避免一次失败把收藏清掉。主动清空时本地已经是空的。
      if (!remote.length && items.length) return;
      adoptedGuestKeys = [];
      items = mergeLists(remote, []);
      indexItems();
      writeList(storageKey(), items);
    }).catch(function () { });
  }

  // 打开页面时拉服务端列表，和本地合并。服务端少了的条目会写回去。
  // 已登录读顾客 metafield；未登录用 session_id 读 GuestWishlist。
  function pullAndMerge() {
    if (!config.proxyPath) return Promise.resolve();
    if (!config.customerId && !ensureGuestSessionId()) return Promise.resolve();
    return fetchRemote('GET').then(function (data) {
      var remote = data && Array.isArray(data.items) ? data.items.map(itemFromStored).filter(Boolean) : [];
      var merged = mergeLists(remote, items);
      var changed = signature(merged) !== signature(remote);
      items = merged;
      indexItems();
      writeList(storageKey(), items);
      paintAll();
      renderPage();
      if (changed) return persist();
    });
  }

  // 页面打开时读本地。已登录把游客列表并进来；未登录把旧的完整对象改写成 key。
  function loadInitial() {
    adoptLegacy(guestKey(), STORAGE_PREFIX + 'guest');
    if (config.customerId) {
      adoptLegacy(storageKey(), STORAGE_PREFIX + 'c:' + config.customerId);
      var userItems = readList(storageKey());
      var guestItems = readList(guestKey());
      items = guestItems.length ? mergeLists(userItems, guestItems) : userItems;
      writeList(storageKey(), items);
      if (guestItems.length) {
        // 这些收藏在游客点击时已经按 session 记过 add，并入账号时不要再记一次。
        adoptedGuestKeys = storedKeys(guestItems);
        try { localStorage.removeItem(guestKey()); } catch (e) { }
      }
    } else {
      items = readList(guestKey());
      // 旧数据是完整对象。读进来后立刻改写成 key 列表。
      writeList(guestKey(), items);
    }
    indexItems();
  }

  // 串行执行，避免连续点击和登录同步互相覆盖。
  function enqueue(task) {
    chain = chain.then(function () {
      return task();
    }).catch(function () {
      paintAll();
      renderPage();
    });
    return chain;
  }

  // keepPage：收藏列表上反选只改心形和存储，卡片留在当前页。刷新或重新进入才按存储重画，这条就不见了。
  function finishPaint(keepPage) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-wishlist-toggle]'), paintButton);
    paintCounts();
    if (!keepPage) renderPage();
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: items.slice() } }));
  }

  // 和 Birdy Grey 的 first_favorite_notification_shown 相同，后面加上店铺名，避免多店共用一个标记。
  function tipKey() {
    return 'first_favorite_notification_shown:' + storeSlug();
  }

  // 只看当前标签页。读不到 sessionStorage 时当成已经提示过，避免反复弹。
  function tipSeen() {
    try {
      return sessionStorage.getItem(tipKey()) === 'true';
    } catch (e) {
      return true;
    }
  }

  // 本标签页已经提示过第一次收藏。
  function markTipSeen() {
    try {
      sessionStorage.setItem(tipKey(), 'true');
    } catch (e) { }
  }

  // 从页面上拿掉第一次收藏提示。
  function removeTipNode(node) {
    if (node && node.parentNode) node.parentNode.removeChild(node);
  }

  // 收起第一次收藏提示。减少动效时立刻移除，否则等淡出。
  function hideTip() {
    var tip = activeTip;
    if (!tip) return;
    activeTip = null;
    if (tipTimer) {
      clearTimeout(tipTimer);
      tipTimer = 0;
    }
    tip.classList.add('is-hiding');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      removeTipNode(tip);
      return;
    }
    setTimeout(function () {
      removeTipNode(tip);
    }, 250);
  }

  // 提示和上限弹窗共用的关闭图标。
  function closeIcon() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M4 4l8 8M12 4l-8 8');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    svg.appendChild(path);
    return svg;
  }

  // 和主题手机导航同一档：999px 及以下算移动端。
  function mobileLayout() {
    return window.matchMedia && window.matchMedia('(max-width: 999px)').matches;
  }

  // 空列表第一次收藏成功后弹出。桌面在心形下面，移动端贴在页面底部。未登录引导建账号，已登录指向收藏页。
  function showFirstTip(button) {
    if (tipSeen() || !button || !button.parentElement) return;
    markTipSeen();
    hideTip();
    var mobile = mobileLayout();

    var loggedIn = !!config.customerId;
    var tip = document.createElement('div');
    tip.className = 'wishlist-first-tip';
    tip.setAttribute('role', 'status');

    var text = document.createElement('p');
    text.className = 'wishlist-first-tip__text';
    var lead = document.createElement('span');
    lead.className = 'wishlist-first-tip__lead';
    lead.textContent = label('firstSavedLead', 'First item saved!');
    text.appendChild(lead);
    text.appendChild(document.createTextNode(' '));

    var link = document.createElement('a');
    link.className = 'wishlist-first-tip__link';
    if (loggedIn) {
      text.appendChild(document.createTextNode(label('firstSavedMember', 'Find it in') + ' '));
      link.href = config.pageUrl || '/pages/wishlist';
      link.textContent = label('firstSavedMemberLink', 'your favorites');
    } else {
      text.appendChild(document.createTextNode(label('firstSavedGuest', 'Save 6 items before being required to') + ' '));
      link.href = config.loginUrl || '/account/register';
      link.textContent = label('firstSavedGuestLink', 'create an account');
    }
    text.appendChild(link);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'wishlist-first-tip__close';
    close.setAttribute('aria-label', label('firstSavedClose', 'Close'));
    close.appendChild(closeIcon());
    close.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      hideTip();
    });

    tip.appendChild(text);
    if (!mobile) tip.appendChild(close);

    if (mobile) {
      tip.classList.add('wishlist-first-tip--toast');
      document.body.appendChild(tip);
    } else {
      var parent = button.parentElement;
      parent.appendChild(tip);
      var end = parent.clientWidth - button.offsetLeft - button.offsetWidth;
      tip.style.top = (button.offsetTop + button.offsetHeight + 8) + 'px';
      tip.style.insetInlineEnd = Math.max(8, end) + 'px';
    }
    activeTip = tip;
    tipTimer = setTimeout(hideTip, TIP_VISIBLE_MS);
  }

  // 已登录不限制。未登录用配置里的 guestLimit，缺省 6。
  function guestMax() {
    if (config.customerId) return Infinity;
    return config.guestLimit;
  }

  // 关掉第 7 个收藏的登录引导，并恢复页面滚动。
  function hideLimitModal() {
    if (!limitModal) return;
    limitModal.remove();
    limitModal = null;
    document.body.style.overflow = bodyOverflow;
  }

  // 上限弹窗每条说明前面的小红心。
  function limitHeart() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 20 18');
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '12');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute('d', 'M1.67 9.67C-.85 6.02.95 1.03 4.71.56 7.71.19 9.26 2.91 9.89 4.15c.47-.93 1.78-4.12 5.25-3.59 4.35.66 5.52 5.2 3.04 9.11-2.17 3.43-5.17 5.53-8.3 7.55-1.72-1.09-5.65-3.82-8.22-7.55Z');
    svg.appendChild(path);
    return svg;
  }

  // 未登录已经有 6 个、再收藏第 7 个时的登录引导。这一下不写入。
  function showLimitModal() {
    if (limitModal) return;
    hideTip();

    var root = document.createElement('div');
    root.className = 'wishlist-limit';

    var overlay = document.createElement('button');
    overlay.type = 'button';
    overlay.className = 'wishlist-limit__overlay';
    overlay.setAttribute('aria-label', label('limitClose', 'Close'));
    overlay.addEventListener('click', hideLimitModal);

    var dialog = document.createElement('div');
    dialog.className = 'wishlist-limit__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'wishlist-limit-title');

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'wishlist-limit__close';
    close.setAttribute('aria-label', label('limitClose', 'Close'));
    close.appendChild(closeIcon());
    close.addEventListener('click', hideLimitModal);

    var title = document.createElement('p');
    title.id = 'wishlist-limit-title';
    title.className = 'wishlist-limit__title';
    title.textContent = label('limitTitle', "You've Hit Your Max");

    var lead = document.createElement('p');
    lead.className = 'wishlist-limit__lead';
    lead.textContent = label('limitLead', 'Unlock More With An Account');

    var list = document.createElement('ul');
    list.className = 'wishlist-limit__list';
    [label('limitSave', 'Save unlimited items'), label('limitShare', 'Share them with your wedding party'), label('limitAccess', 'Access them on any device')].forEach(function (text) {
      var item = document.createElement('li');
      var icon = document.createElement('span');
      icon.className = 'wishlist-limit__heart';
      icon.appendChild(limitHeart());
      item.appendChild(icon);
      item.appendChild(document.createTextNode(text));
      list.appendChild(item);
    });

    var action = document.createElement('button');
    action.type = 'button';
    action.className = 'wishlist-limit__action';
    action.textContent = label('limitAction', 'Sign In Or Create Account');
    action.addEventListener('click', function () {
      hideLimitModal();
      openAccount();
    });

    dialog.appendChild(close);
    dialog.appendChild(title);
    dialog.appendChild(lead);
    dialog.appendChild(list);
    dialog.appendChild(action);
    root.appendChild(overlay);
    root.appendChild(dialog);
    document.body.appendChild(root);

    bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    limitModal = root;
    close.focus();
  }

  // 未登录没有调整顺序。分享页只展示收到的列表，分享和排序都藏起来。
  function syncMemberActions() {
    var reorder = document.querySelector('[data-wishlist-reorder]');
    var share = document.querySelector('[data-wishlist-share]');
    var viewingShare = sharedList != null;
    if (reorder) {
      if (!viewingShare && config.customerId) reorder.removeAttribute('hidden');
      else reorder.setAttribute('hidden', '');
    }
    if (share) {
      if (viewingShare) share.setAttribute('hidden', '');
      else share.removeAttribute('hidden');
    }
  }

  // 未登录点分享：提示建账号。按钮直接打开登录弹框。
  function showShareModal() {
    if (limitModal) return;
    hideTip();

    var root = document.createElement('div');
    root.className = 'wishlist-limit wishlist-limit--share';

    var overlay = document.createElement('button');
    overlay.type = 'button';
    overlay.className = 'wishlist-limit__overlay';
    overlay.setAttribute('aria-label', label('shareClose', 'Close'));
    overlay.addEventListener('click', hideLimitModal);

    var dialog = document.createElement('div');
    dialog.className = 'wishlist-limit__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'wishlist-share-title');

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'wishlist-limit__close';
    close.setAttribute('aria-label', label('shareClose', 'Close'));
    close.appendChild(closeIcon());
    close.addEventListener('click', hideLimitModal);

    var title = document.createElement('p');
    title.id = 'wishlist-share-title';
    title.className = 'wishlist-limit__title';
    title.textContent = label('shareTitle', 'Want To Share Your Favorites?');

    var text = document.createElement('p');
    text.className = 'wishlist-share-gate__text';
    text.textContent = label('shareText', 'Create an account to send your saved styles to your wedding party, gather feedback, and keep everyone in sync.');

    var action = document.createElement('button');
    action.type = 'button';
    action.className = 'wishlist-limit__action';
    action.textContent = label('shareAction', 'Sign In Or Create Account');
    action.addEventListener('click', function () {
      hideLimitModal();
      openAccount();
    });

    dialog.appendChild(close);
    dialog.appendChild(title);
    dialog.appendChild(text);
    dialog.appendChild(action);
    root.appendChild(overlay);
    root.appendChild(dialog);
    document.body.appendChild(root);

    bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    limitModal = root;
    close.focus();
  }

  // 点心形：已收藏就移除，未收藏就写入。未登录已满 6 个时只弹登录框，
  // 并把这第 7 个记下来，登录/注册后自动补进该客户名下。
  function toggle(button) {
    var item = readItem(button);
    if (!item) return;
    if (button.getAttribute('data-wishlist-state') === 'loading') return;

    var shouldRemove = !!byKey[item.key];
    if (!shouldRemove && items.length >= guestMax()) {
      writePending(item.key);
      showLimitModal();
      return;
    }
    var keepPage = !!(button.closest && button.closest('[data-wishlist-page]'));
    var started = Date.now();
    markKeyLoading(item.key);

    enqueue(function () {
      // 前面的点击可能刚把未登录列表加满，这里再看一次，避免连点超出上限。
      if (!shouldRemove && items.length >= guestMax()) {
        writePending(item.key);
        return waitAtLeast(started).then(function () {
          finishPaint(keepPage);
          showLimitModal();
        });
      }
      // 列表长度以进队列时为准：页面打开时已从本店 localStorage（登录后再并上账号列表）灌进 items。
      // 数量为 0 才是第一次；同一标签页提示过一次后，清空再收藏也不再弹。
      var firstSave = !shouldRemove && items.length === 0 && !tipSeen();
      if (shouldRemove) {
        items = items.filter(function (entry) { return entry.key !== item.key; });
      } else {
        var stored = itemFromStored(item.key);
        if (!stored) {
          return waitAtLeast(started).then(function () {
            finishPaint(keepPage);
          });
        }
        items = [stored].concat(items.filter(function (entry) { return entry.key !== stored.key; }));
        if (items.length > config.maxItems) items = items.slice(0, config.maxItems);
      }
      indexItems();
      return persist({
        action: shouldRemove ? 'remove' : 'add',
        key: item.key
      }).then(function () {
        return waitAtLeast(started);
      }).then(function () {
        finishPaint(keepPage);
        // 这条后来正常收藏成功了，撤销待补标记，登录后不会再多补一次。
        if (!shouldRemove && byKey[item.key]) clearPending(item.key);
        if (firstSave && byKey[item.key]) showFirstTip(button);
      });
    });
  }

  // 商品 metafield（product_name、评论、色圈）不在 product.js 里，用 section 渲染拿和列表页同一份数据。
  function loadProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = fetch(rootPrefix() + '/products/' + encodeURIComponent(handle) + '?section_id=wishlist-product', {
        credentials: 'same-origin',
        cache: 'no-store'
      }).then(function (response) {
        if (!response.ok) throw new Error('missing product');
        return response.text();
      }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-wishlist-product]');
        if (!node || !node.textContent) return null;
        return JSON.parse(node.textContent);
      }).catch(function () {
        return null;
      });
    }
    return productCache[handle];
  }

  // 收藏里只有颜色片段。用和 key 相同的 handleize 对上色名，不能只比小写（light blue 对不上 light-blue）。
  function findColor(colors, item) {
    if (!colors || !item) return null;
    var handle = item.colorHandle || handleize(item.color || '');
    for (var i = 0; i < colors.length; i++) {
      if (handleize(colors[i].name || '') === handle) return colors[i];
    }
    return null;
  }

  // 用商品 ID 查出 handle。predictive search 一次最多 10 个。
  function lookupHandles(ids) {
    var unique = [];
    (ids || []).forEach(function (id) {
      var value = String(id || '');
      if (value && unique.indexOf(value) < 0) unique.push(value);
    });
    if (!unique.length) return Promise.resolve({});

    var jobs = [];
    for (var i = 0; i < unique.length; i += 10) jobs.push(unique.slice(i, i + 10));

    return Promise.all(jobs.map(function (chunk) {
      var query = chunk.map(function (id) { return 'id:' + id; }).join(' OR ');
      var url = rootPrefix() + '/search/suggest.json?resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last&q=' + encodeURIComponent(query);
      return fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then(function (response) {
        return response.ok ? response.json() : null;
      }).then(function (data) {
        var map = {};
        var products = data && data.resources && data.resources.results && data.resources.results.products || [];
        products.forEach(function (product) {
          if (!product || product.id == null || !product.handle) return;
          var image = product.featured_image && product.featured_image.url ? product.featured_image.url : product.image;
          map[String(product.id)] = {
            handle: String(product.handle),
            image: absoluteImage(image)
          };
        });
        return map;
      }).catch(function () {
        return {};
      });
    })).then(function (maps) {
      var all = {};
      maps.forEach(function (map) {
        Object.keys(map).forEach(function (id) { all[id] = map[id]; });
      });
      return all;
    });
  }

  // 评分保留一位小数。不是数字就留空。
  function formatRating(value) {
    var number = Number(value);
    if (!isFinite(number)) return '';
    return number.toFixed(1);
  }

  // 配置里的图标地址：心形、加载中、评分星星。
  function iconUrl(name) {
    return (config.icons && config.icons[name]) || '';
  }

  // 列表卡片上的心形。颜色先用片段，商品详情回来后再改成色名。
  function createButton(item) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'wishlist-button';
    button.setAttribute('data-wishlist-toggle', '');
    button.setAttribute('data-wishlist-context', 'product');
    button.setAttribute('data-wishlist-state', 'saved');
    button.setAttribute('data-product-id', item.productId);
    if (item.handle) button.setAttribute('data-product-handle', item.handle);
    button.setAttribute('data-color', item.color || item.colorHandle || '');
    if (item.variantId) button.setAttribute('data-variant-id', item.variantId);
    if (item.image) button.setAttribute('data-image', item.image);
    if (item.handle) button.setAttribute('data-url', item.url || productPath(item.handle, item.variantId));
    button.setAttribute('aria-pressed', 'true');
    button.setAttribute('aria-label', label('saved', 'Remove from favorites'));
    button.setAttribute('data-wishlist-tip', label('saved', 'Remove from favorites'));

    ['unsaved', 'saved', 'loading'].forEach(function (name) {
      var img = document.createElement('img');
      img.className = 'wishlist-button__icon wishlist-button__icon--' + name;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      img.src = iconUrl(name);
      button.appendChild(img);
    });
    return button;
  }

  // 排序模式左上角的六点把手。平时藏着，点 Reorder 才出现。
  function createDragHandle() {
    var handle = document.createElement('button');
    handle.type = 'button';
    handle.className = 'wishlist-card__handle';
    handle.setAttribute('aria-label', label('reorderHandle', 'Drag to reorder'));
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 12 16');
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '16');
    svg.setAttribute('aria-hidden', 'true');
    [[3, 2.5], [9, 2.5], [3, 8], [9, 8], [3, 13.5], [9, 13.5]].forEach(function (point) {
      var dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      dot.setAttribute('cx', String(point[0]));
      dot.setAttribute('cy', String(point[1]));
      dot.setAttribute('r', '1.15');
      dot.setAttribute('fill', 'currentColor');
      svg.appendChild(dot);
    });
    handle.appendChild(svg);
    return handle;
  }

  function saveOrderIcon() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'wishlist-page__check');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M3.2 8.2 6.3 11.3 12.8 4.7');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    return svg;
  }

  // 先画出空卡片。名称、色圈、价格等查询回来后再填。
  function createCard(item) {
    var card = document.createElement('article');
    card.className = 'wishlist-card';
    card.setAttribute('data-wishlist-key', item.key);

    var href = item.handle ? (item.url || productPath(item.handle, item.variantId)) : '#';
    var wrap = document.createElement('div');
    wrap.className = 'wishlist-card__media-wrap';

    var media = document.createElement('a');
    media.className = 'wishlist-card__media';
    media.href = href;
    var watermark = document.createElement('span');
    watermark.className = 'wishlist-card__watermark';
    watermark.setAttribute('aria-hidden', 'true');
    watermark.textContent = config.store || '';
    media.appendChild(watermark);
    console.log(item)
    if (item.image) {
      var img = document.createElement('img');
      img.src = item.image;
      img.alt = item.color || item.colorHandle || '';
      img.loading = 'lazy';
      media.appendChild(img);
    }
    wrap.appendChild(media);
    wrap.appendChild(createDragHandle());
    wrap.appendChild(createButton(item));
    card.appendChild(wrap);

    var meta = document.createElement('div');
    meta.className = 'wishlist-card__meta';

    var colorRow = document.createElement('div');
    colorRow.className = 'wishlist-card__color-row';

    var color = document.createElement('div');
    color.className = 'wishlist-card__color';
    var swatch = document.createElement('span');
    swatch.className = 'wishlist-card__swatch';
    swatch.hidden = true;
    var colorName = document.createElement('span');
    colorName.className = 'wishlist-card__color-name';
    colorName.textContent = item.color || '';
    color.appendChild(swatch);
    color.appendChild(colorName);
    colorRow.appendChild(color);

    var reviews = document.createElement('a');
    reviews.className = 'wishlist-card__reviews';
    reviews.hidden = true;
    colorRow.appendChild(reviews);
    meta.appendChild(colorRow);

    var copy = document.createElement('div');
    copy.className = 'wishlist-card__copy';

    var name = document.createElement('a');
    name.className = 'wishlist-card__name';
    name.href = href;
    name.textContent = item.title || '';
    copy.appendChild(name);

    var subtitle = document.createElement('a');
    subtitle.className = 'wishlist-card__subtitle';
    subtitle.href = href;
    subtitle.hidden = true;
    copy.appendChild(subtitle);
    meta.appendChild(copy);

    var price = document.createElement('div');
    price.className = 'wishlist-card__price price-list';
    price.setAttribute('data-wishlist-price', '');
    meta.appendChild(price);
    card.appendChild(meta);
    return card;
  }

  // 搜索接口和 section 都可能给出 // 开头的地址。页面是 http 时会去请求 http 图片，补成 https。
  function absoluteImage(src) {
    if (!src) return '';
    var url = String(src);
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  // 把商品图铺进卡片。背景和 img 都写上，避免图片节点高度为 0 时看起来是空白。
  function fillImage(card, item, src) {
    src = absoluteImage(src);
    if (!src) return;
    var media = card.querySelector('.wishlist-card__media');
    if (!media) return;
    media.style.backgroundImage = 'url("' + src.replace(/"/g, '') + '")';
    var img = media.querySelector('img');
    if (!img) {
      img = document.createElement('img');
      img.alt = item.color || '';
      media.appendChild(img);
    }
    if (img.getAttribute('src') !== src) img.src = src;
  }

  // subtitle 有值时只显示它。没有时仍用 product_name 做名称、title 做副标题。色圈只留收藏的那一个颜色。
  function fillCard(card, item, product) {
    var price = card.querySelector('[data-wishlist-price]');
    if (!price) return;
    if (!product) {
      price.textContent = '';
      var unavailable = document.createElement('p');
      unavailable.className = 'wishlist-card__unavailable';
      unavailable.textContent = label('unavailable', 'This item is no longer available');
      price.replaceWith(unavailable);
      return;
    }

    var metafieldSubtitle = String(product.subtitle || '').trim();
    var productName = String(product.productName || '').trim();
    var title = String(product.title || item.title || '').trim();
    var name = card.querySelector('.wishlist-card__name');
    var subtitle = card.querySelector('.wishlist-card__subtitle');
    var colorInfo = findColor(product.colors, item);
    var displayColor = (colorInfo && colorInfo.name) || item.color || '';
    var displayName = metafieldSubtitle
      ? metafieldSubtitle.split('{color}').join(String(displayColor || '').trim()).replace(/\s+/g, ' ').trim()
      : (productName || title);
    if (name) name.textContent = displayName;
    if (subtitle) {
      if (!metafieldSubtitle && productName && title) {
        subtitle.hidden = false;
        subtitle.textContent = title;
      } else {
        subtitle.hidden = true;
        subtitle.textContent = '';
      }
    }
    var variantId = (colorInfo && colorInfo.variantId) || item.variantId || '';
    var href = item.handle ? productPath(item.handle, variantId) : '';
    if (href) {
      var media = card.querySelector('.wishlist-card__media');
      if (media) media.href = href;
      if (name) name.href = href;
      if (subtitle) subtitle.href = href;
    }
    var button = card.querySelector('[data-wishlist-toggle]');
    if (button) {
      if (item.handle) button.setAttribute('data-product-handle', item.handle);
      if (displayColor && handleize(displayColor) === item.colorHandle) button.setAttribute('data-color', displayColor);
      if (variantId) button.setAttribute('data-variant-id', String(variantId));
      if (colorInfo && colorInfo.image) button.setAttribute('data-image', colorInfo.image);
      if (href) button.setAttribute('data-url', href);
    }

    var colorName = card.querySelector('.wishlist-card__color-name');
    var swatch = card.querySelector('.wishlist-card__swatch');
    if (colorName) colorName.textContent = displayColor;
    if (swatch && colorInfo && colorInfo.style) {
      swatch.hidden = false;
      swatch.setAttribute('style', colorInfo.style);
      if (colorInfo.white) swatch.classList.add('wishlist-card__swatch--white');
    }
    item.color = displayColor;
    // 没有颜色，或该颜色变体没有图时，用商品主图。::default 就是没颜色的收藏。
    fillImage(card, item, (colorInfo && colorInfo.image) || product.image || item.image);
    var photo = card.querySelector('.wishlist-card__media img');
    if (photo) photo.alt = displayName + (displayColor ? ', ' + displayColor : '');

    var reviews = card.querySelector('.wishlist-card__reviews');
    if (reviews && product.rating != null && Number(product.ratingCount) > 0) {
      reviews.hidden = false;
      var reviewUrl = href || productPath(item.handle, variantId);
      reviews.href = reviewUrl.split('#')[0] + '#product-reviews';
      var star = document.createElement('img');
      star.src = iconUrl('star');
      star.alt = '';
      star.width = 12;
      star.height = 12;
      var value = document.createElement('span');
      value.textContent = formatRating(product.rating);
      var count = document.createElement('span');
      count.textContent = '(' + product.ratingCount + ')';
      reviews.appendChild(star);
      reviews.appendChild(value);
      reviews.appendChild(count);
    }

    price.innerHTML = product.priceHtml || '';
  }

  // 收藏为空时藏起米色标题栏。display:flex 会盖掉 hidden，所以用 hidden 属性配合样式。
  function setWishlistHero(visible) {
    Array.prototype.forEach.call(document.querySelectorAll('.wishlist-page__hero'), function (hero) {
      if (visible) hero.removeAttribute('hidden');
      else hero.setAttribute('hidden', '');
    });
  }

  // 列表页按 key 里的商品 ID 查出 handle，再拉 wishlist-product 补颜色、图片和价格。
  function renderPage() {
    var page = document.querySelector('[data-wishlist-page]');
    if (!page) return;
    var list = page.querySelector('[data-wishlist-list]');
    var empty = page.querySelector('[data-wishlist-empty]');
    if (!list) return;

    var token = ++renderToken;
    var source = sharedList == null ? items : sharedList;
    if (shareLoading) {
      list.setAttribute('hidden', '');
      if (empty) empty.setAttribute('hidden', '');
      return;
    }
    if (!source.length) {
      list.textContent = '';
      list.setAttribute('hidden', '');
      if (empty) empty.removeAttribute('hidden');
      // 没有收藏时不显示标题和 Share / Reorder。
      setWishlistHero(false);
      return;
    }

    setWishlistHero(true);
    if (empty) empty.setAttribute('hidden', '');
    list.removeAttribute('hidden');
    list.textContent = '';

    var cards = source.map(function (item) {
      var card = createCard(item);
      list.appendChild(card);
      var button = card.querySelector('[data-wishlist-toggle]');
      if (button) paintButton(button);
      return { card: card, item: item };
    });

    function fillEntry(entry, product) {
      if (token !== renderToken) return;
      if (!entry.card.isConnected) return;
      fillCard(entry.card, entry.item, product);
    }

    lookupHandles(cards.map(function (entry) { return entry.item.productId; })).then(function (handles) {
      cards.forEach(function (entry) {
        var found = handles[entry.item.productId];
        var handle = found && found.handle;
        if (!handle) {
          fillEntry(entry, null);
          return;
        }
        entry.item.handle = handle;
        if (found.image) {
          entry.item.image = found.image;
          fillImage(entry.card, entry.item, found.image);
        }
        loadProduct(handle).then(function (product) {
          fillEntry(entry, product);
        });
      });
    });
  }

  // 拼选择器时转义表单 id，避免颜色名或特殊字符把查询打断。
  function cssEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/"/g, '');
  }

  // 详情页切换颜色或尺码时，心形改跟当前颜色。尺码变化不新开一条收藏。
  function onVariantChanged(event) {
    var variant = event.detail && event.detail.variant;
    var form = event.target;
    // 表单里有 name="id" 的变体输入，form.id 会指向那个 input，不能拿来当表单 id。
    var formId = form && form.getAttribute && form.getAttribute('id');
    if (!variant || !formId) return;

    var medias = document.querySelectorAll('product-media[form-id="' + cssEscape(formId) + '"]');
    Array.prototype.forEach.call(medias, function (media) {
      var button = media.querySelector('[data-wishlist-toggle]');
      if (!button) return;
      var index = parseInt(button.getAttribute('data-color-option-index'), 10);
      if (index && variant.options && variant.options[index - 1]) {
        button.setAttribute('data-color', variant.options[index - 1]);
      }
      if (variant.id) button.setAttribute('data-variant-id', String(variant.id));

      var image = '';
      if (variant.featured_image && variant.featured_image.src) image = variant.featured_image.src;
      else if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
        image = variant.featured_media.preview_image.src;
      }
      if (image) button.setAttribute('data-image', image);

      var handle = button.getAttribute('data-product-handle');
      if (handle) button.setAttribute('data-url', productPath(handle, variant.id));
      if (button.getAttribute('data-wishlist-state') !== 'loading') paintButton(button);
    });
  }

  var SHARE_BASE32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

  function encodeUtf8(text) {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text);
    var binary = unescape(encodeURIComponent(text));
    var out = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }

  function decodeUtf8(bytes) {
    if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(bytes);
    var binary = '';
    for (var i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return decodeURIComponent(escape(binary));
  }

  // 大小写不敏感。店铺会把路径收成小写，base64 会解不开。
  function bytesToBase32(bytes) {
    var out = '';
    var buffer = 0;
    var bits = 0;
    for (var i = 0; i < bytes.length; i++) {
      buffer = (buffer << 8) | bytes[i];
      bits += 8;
      while (bits >= 5) {
        bits -= 5;
        out += SHARE_BASE32[(buffer >> bits) & 31];
      }
    }
    if (bits > 0) out += SHARE_BASE32[(buffer << (5 - bits)) & 31];
    return out;
  }

  function base32ToBytes(text) {
    var clean = String(text || '').toUpperCase().replace(/[^A-Z2-7]/g, '');
    var out = [];
    var buffer = 0;
    var bits = 0;
    for (var i = 0; i < clean.length; i++) {
      var value = SHARE_BASE32.indexOf(clean.charAt(i));
      if (value < 0) continue;
      buffer = (buffer << 5) | value;
      bits += 5;
      if (bits >= 8) {
        bits -= 8;
        out.push((buffer >> bits) & 255);
      }
    }
    return new Uint8Array(out);
  }

  // 分享人的名字。没有名字时分享页仍用 Shared Favorites。
  function shareName() {
    return String(config.customerName || '').trim();
  }

  // 新链接带上名字。旧链接只有 key 数组，两种都能解开。
  function sharePayload(list) {
    var payload = { items: storedKeys(list) };
    var name = shareName();
    if (name) payload.name = name;
    return payload;
  }

  // 分享码里直接带上 key 列表，收到链接的人不用登录也能查到这些商品。
  function shareCode(list) {
    return 's1' + bytesToBase32(encodeUtf8(JSON.stringify(sharePayload(list))));
  }

  function decodeShare(code) {
    if (!code || !/^s1/i.test(code)) return null;
    try {
      var raw = JSON.parse(decodeUtf8(base32ToBytes(code.slice(2))));
      var items = Array.isArray(raw) ? raw : raw && raw.items;
      if (!Array.isArray(items)) return null;
      return {
        items: items.map(itemFromStored).filter(Boolean),
        name: raw && raw.name ? String(raw.name) : ''
      };
    } catch (e) {
      return null;
    }
  }

  function wishlistPath() {
    try {
      return new URL(config.pageUrl || '/pages/wishlist', window.location.origin).pathname.replace(/\/+$/, '');
    } catch (e) {
      return '/pages/wishlist';
    }
  }

  // /pages/wishlist/{code}。没有 code 时是自己的收藏页。短码约 20 位，s1 开头是旧的长码。
  function shareCodeFromLocation() {
    var path = window.location.pathname.replace(/\/+$/, '');
    var prefix = wishlistPath() + '/';
    if (path.toLowerCase().indexOf(prefix.toLowerCase()) !== 0) return '';
    var code = path.slice(prefix.length).split('/')[0];
    try {
      code = decodeURIComponent(code);
    } catch (e) {}
    if (/^s1/i.test(code)) return code;
    if (/^[2-9a-hj-np-z]{16,32}$/i.test(code)) return code;
    return '';
  }

  function shareLink(code) {
    var origin = window.location.origin;
    try {
      origin = new URL(config.pageUrl || '/pages/wishlist', window.location.origin).origin;
    } catch (e) {}
    return origin + wishlistPath() + '/' + code;
  }

  // 有分享人名字时用 “义's Favorites”，否则用 Shared Favorites。
  function sharedHeading(name) {
    var text = String(name || '').trim();
    if (!text) return label('sharedTitle', 'Shared Favorites');
    return label('sharedNamedTitle', "{name}'s Favorites").replace('{name}', text);
  }

  function markShared(list, name) {
    sharedList = list;
    var title = document.querySelector('.wishlist-page__title');
    if (title) title.textContent = sharedHeading(name);
    var empty = document.querySelector('[data-wishlist-empty] p');
    if (empty && !sharedList.length) empty.textContent = label('sharedEmpty', "This shared list isn't available.");
  }

  // 短码向 App Proxy 换回 key 列表。s1 长码仍在本地解开，已经发出去的链接不用改。
  function loadSharedList() {
    var code = shareCodeFromLocation();
    if (!code) {
      sharedList = null;
      shareLoading = false;
      return Promise.resolve();
    }
    if (/^s1/i.test(code)) {
      shareLoading = false;
      var decoded = decodeShare(code);
      markShared(decoded && decoded.items ? decoded.items : [], decoded && decoded.name);
      return Promise.resolve();
    }
    shareLoading = true;
    sharedList = [];
    var title = document.querySelector('.wishlist-page__title');
    if (title) title.textContent = label('sharedTitle', 'Shared Favorites');
    return fetchWithTimeout(config.proxyPath + '/shares/' + encodeURIComponent(code), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('share');
      return response.json();
    }).then(function (data) {
      var raw = data && Array.isArray(data.items) ? data.items : [];
      shareLoading = false;
      markShared(raw.map(itemFromStored).filter(Boolean), data && data.name);
    }).catch(function () {
      shareLoading = false;
      markShared([]);
    });
  }

  // 已登录才向服务端要短码。接口还没有时退回 s1 长码，分享不会失败。
  function requestShareCode(list) {
    var keys = storedKeys(list);
    if (!config.customerId || !config.proxyPath) return Promise.resolve(shareCode(list));
    var body = { version: 1, items: keys };
    if (shareName()) body.name = shareName();
    return fetchWithTimeout(config.proxyPath + '/shares', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error('share');
      return response.json();
    }).then(function (data) {
      if (!data || !data.id) throw new Error('share');
      return String(data.id);
    }).catch(function () {
      return shareCode(list);
    });
  }

  // 少于 3 个全部露出。4 个及以上只留前 3 张，最后一格用 +(总数-3)。
  function sharePreview(list) {
    var photos = list || [];
    if (photos.length >= 4) {
      return { photos: photos.slice(0, 3), more: photos.length - 3 };
    }
    return { photos: photos, more: 0 };
  }

  // 卡片上已经查到的图优先，没有再退回存储里的地址。
  function shareImage(item) {
    var card = item && item.key && document.querySelector('[data-wishlist-key="' + cssEscape(item.key) + '"] .wishlist-card__media img');
    var src = card && (card.currentSrc || card.getAttribute('src'));
    if (!src && item && item.image) src = item.image;
    return absoluteImage(src || '');
  }

  function sharePhoto(item) {
    var cell = document.createElement('div');
    cell.className = 'wishlist-share__photo';
    var src = shareImage(item);
    if (!src) return cell;
    var img = document.createElement('img');
    img.alt = '';
    img.src = src;
    cell.appendChild(img);
    return cell;
  }

  // 已登录点分享：按设计稿展示商品图和可复制链接。
  function showShareLinkModal() {
    if (limitModal) return;
    hideTip();

    var url = '';
    var preview = sharePreview(items);
    var root = document.createElement('div');
    root.className = 'wishlist-share';
    root.addEventListener('click', function (event) {
      if (event.target === root) hideLimitModal();
    });

    var dialog = document.createElement('div');
    dialog.className = 'wishlist-share__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'wishlist-share-link-title');

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'wishlist-share__close';
    close.setAttribute('aria-label', label('shareClose', 'Close'));
    var closeSrc = iconUrl('shareClose');
    if (closeSrc) {
      var closeImg = document.createElement('img');
      closeImg.alt = '';
      closeImg.src = closeSrc;
      close.appendChild(closeImg);
    } else {
      close.appendChild(closeIcon());
    }
    close.addEventListener('click', hideLimitModal);

    var title = document.createElement('p');
    title.id = 'wishlist-share-link-title';
    title.className = 'wishlist-share__title';
    title.textContent = label('shareLinkTitle', 'Share Your Favorites');

    var photos = document.createElement('div');
    photos.className = 'wishlist-share__photos';
    preview.photos.forEach(function (item) {
      photos.appendChild(sharePhoto(item));
    });
    if (preview.more > 0) {
      var more = document.createElement('div');
      more.className = 'wishlist-share__more';
      more.textContent = label('shareMore', '+{count} items').replace('{count}', String(preview.more));
      photos.appendChild(more);
    }

    var box = document.createElement('div');
    box.className = 'wishlist-share__linkbox';
    var linkLabel = document.createElement('p');
    linkLabel.className = 'wishlist-share__label';
    linkLabel.textContent = label('shareLinkLabel', 'LINK');
    var fields = document.createElement('div');
    fields.className = 'wishlist-share__fields';
    var row = document.createElement('div');
    row.className = 'wishlist-share__row';
    var input = document.createElement('input');
    input.className = 'wishlist-share__url';
    input.type = 'text';
    input.readOnly = true;
    input.value = '';
    input.placeholder = label('shareCreating', 'Creating link…');
    input.setAttribute('aria-label', label('shareLinkLabel', 'LINK'));
    var copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'wishlist-share__copy';
    copy.textContent = label('shareCopy', 'Copy');
    copy.disabled = true;
    requestShareCode(items).then(function (code) {
      url = shareLink(code);
      input.value = url;
      input.placeholder = '';
      copy.disabled = false;
    });
    copy.addEventListener('click', function () {
      if (!url) return;
      var previous = copy.textContent;
      var mark = function () {
        copy.textContent = label('shareCopied', 'Copied');
        window.setTimeout(function () {
          copy.textContent = previous;
        }, 1500);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(mark).catch(function () {});
        return;
      }
      input.select();
      try {
        document.execCommand('copy');
        mark();
      } catch (e) {}
    });
    var hint = document.createElement('p');
    hint.className = 'wishlist-share__hint';
    hint.textContent = label('shareHint', 'Heads up! Anyone with the link can view.');

    row.appendChild(input);
    row.appendChild(copy);
    fields.appendChild(row);
    fields.appendChild(hint);
    box.appendChild(linkLabel);
    box.appendChild(fields);
    dialog.appendChild(close);
    dialog.appendChild(title);
    if (preview.photos.length) dialog.appendChild(photos);
    dialog.appendChild(box);
    root.appendChild(dialog);
    document.body.appendChild(root);

    bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    limitModal = root;
    close.focus();
  }

  // 未登录先弹出建账号提示。已登录打开复制链接弹框。
  function sharePage() {
    if (!config.customerId) {
      showShareModal();
      return;
    }
    showShareLinkModal();
  }

  // 点 Reorder 进入拖拽。再点 Save Order 才按页面上的顺序写回本地和账号。
  function setReorderMode(on) {
    reorderMode = on;
    var page = document.querySelector('[data-wishlist-page]');
    if (page) page.classList.toggle('is-reordering', on);
    Array.prototype.forEach.call(document.querySelectorAll('[data-wishlist-reorder]'), function (button) {
      var img = button.querySelector('img');
      var span = button.querySelector('span');
      var mark = button.querySelector('.wishlist-page__check');
      if (!button.getAttribute('data-reorder-label') && span) {
        button.setAttribute('data-reorder-label', span.textContent);
      }
      if (on) {
        if (img) img.hidden = true;
        if (!mark) button.insertBefore(saveOrderIcon(), span);
        if (span) span.textContent = label('saveOrder', 'Save Order');
        button.setAttribute('aria-pressed', 'true');
      } else {
        if (img) img.hidden = false;
        if (mark) mark.remove();
        if (span) span.textContent = button.getAttribute('data-reorder-label') || label('reorder', 'Reorder');
        button.setAttribute('aria-pressed', 'false');
      }
    });
  }

  // 按卡片当前顺序写回收藏，再退出拖拽。远端顺序没变就不用重画。
  function saveOrder() {
    var page = document.querySelector('[data-wishlist-page]');
    var cards = page ? page.querySelectorAll('[data-wishlist-key]') : [];
    var next = [];
    Array.prototype.forEach.call(cards, function (card) {
      var item = byKey[card.getAttribute('data-wishlist-key')];
      if (item && next.indexOf(item) === -1) next.push(item);
    });
    items.forEach(function (item) {
      if (next.indexOf(item) === -1) next.push(item);
    });
    items = next;
    indexItems();
    var saved = storedKeys(items).join('|');
    setReorderMode(false);
    persist().then(function () {
      if (storedKeys(items).join('|') !== saved) renderPage();
    });
  }

  // 第一次进入排序，再点一次保存。分享页和未登录不排序。
  function reorderItems() {
    if (sharedList != null || !config.customerId) return;
    if (reorderMode) {
      saveOrder();
      return;
    }
    if (items.length < 2) return;
    setReorderMode(true);
  }

  var REORDER_MOVE = '280ms cubic-bezier(0.22, 1, 0.36, 1)';

  // 格子换位时从当前位置滑到最终位置。先去掉旧位移再量，避免跨多格时中间闪一下。
  function shiftList(list, move) {
    var before = [];
    Array.prototype.forEach.call(list.children, function (node) {
      if (!node || node.nodeType !== 1 || node.classList.contains('is-dragging')) return;
      before.push({ node: node, rect: node.getBoundingClientRect() });
    });
    before.forEach(function (item) {
      var node = item.node;
      if (node._clearShift) {
        node.removeEventListener('transitionend', node._clearShift);
        node._clearShift = null;
      }
      node.style.transition = 'none';
      node.style.transform = 'none';
    });
    move();
    before.forEach(function (item) {
      var node = item.node;
      if (!node.parentElement) return;
      var next = node.getBoundingClientRect();
      var dx = item.rect.left - next.left;
      var dy = item.rect.top - next.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
        node.style.transition = '';
        node.style.transform = '';
        return;
      }
      node.style.transform = 'translate3d(' + dx + 'px, ' + dy + 'px, 0)';
      var stamp = (node._shiftStamp || 0) + 1;
      node._shiftStamp = stamp;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (node._shiftStamp !== stamp || !node.parentElement) return;
          node.style.transition = 'transform ' + REORDER_MOVE;
          node.style.transform = 'translate3d(0, 0, 0)';
          node._clearShift = function (event) {
            if (event.propertyName !== 'transform' || event.target !== node) return;
            node.style.transition = '';
            node.style.transform = '';
            node.removeEventListener('transitionend', node._clearShift);
            node._clearShift = null;
          };
          node.addEventListener('transitionend', node._clearShift);
          window.setTimeout(function () {
            if (node._shiftStamp !== stamp) return;
            node.style.transition = '';
            node.style.transform = '';
            if (node._clearShift) {
              node.removeEventListener('transitionend', node._clearShift);
              node._clearShift = null;
            }
          }, 360);
        });
      });
    });
  }

  // 布局位置，不算正在滑动的 transform，避免动画过程中来回换位。
  function mediaBox(node) {
    var frame = node.querySelector('.wishlist-card__media-wrap, .wishlist-card__placeholder-frame');
    var target = frame || node;
    var dx = 0;
    var dy = 0;
    var match = (node.style.transform || '').match(/translate3d\(\s*([-\d.]+)px,\s*([-\d.]+)px/);
    if (match) {
      dx = parseFloat(match[1]) || 0;
      dy = parseFloat(match[2]) || 0;
    }
    var rect = target.getBoundingClientRect();
    return {
      left: rect.left - dx,
      top: rect.top - dy,
      width: rect.width,
      height: rect.height,
      right: rect.right - dx,
      bottom: rect.bottom - dy
    };
  }

  // 只和盖过一半的那一张互换。中间的卡片留在原位，不会被往后推。
  function placeDraggedCard(placeholder, card) {
    var list = placeholder.parentElement;
    var home = placeholder._homeOrder;
    if (!list || !card || !home) return;
    var dragRect = mediaBox(card);
    var best = null;
    var bestOverlap = 0;
    home.forEach(function (node) {
      if (node === placeholder || !node.parentElement) return;
      var rect = mediaBox(node);
      var sameRow = Math.abs((dragRect.top + dragRect.height / 2) - (rect.top + rect.height / 2)) < Math.min(dragRect.height, rect.height) * 0.5;
      var overlap = sameRow
        ? Math.max(0, Math.min(dragRect.right, rect.right) - Math.max(dragRect.left, rect.left))
        : Math.max(0, Math.min(dragRect.bottom, rect.bottom) - Math.max(dragRect.top, rect.top));
      var half = (sameRow ? rect.width : rect.height) / 2;
      if (overlap > half && overlap > bestOverlap) {
        bestOverlap = overlap;
        best = node;
      }
    });
    // 盖过一半就锁定这一张。目标挪走后不要立刻弹回，否则会来回闪。
    if (best) placeholder._swapWith = best;
    else if (placeholder._swapWith) {
      var slot = mediaBox(placeholder);
      var centerX = dragRect.left + dragRect.width / 2;
      var centerY = dragRect.top + dragRect.height / 2;
      var inside = centerX >= slot.left && centerX <= slot.right && centerY >= slot.top && centerY <= slot.bottom;
      if (!inside) placeholder._swapWith = null;
    }
    var target = placeholder._swapWith;
    var order = home.slice();
    if (target) {
      var from = order.indexOf(placeholder);
      var to = order.indexOf(target);
      if (from < 0 || to < 0) return;
      order[from] = target;
      order[to] = placeholder;
    }
    var current = [];
    Array.prototype.forEach.call(list.children, function (node) {
      if (!node.classList || node.classList.contains('is-dragging')) return;
      current.push(node);
    });
    var same = current.length === order.length;
    for (var i = 0; same && i < order.length; i++) {
      if (current[i] !== order[i]) same = false;
    }
    if (same) return;
    shiftList(list, function () {
      order.forEach(function (node) {
        list.appendChild(node);
      });
    });
  }

  // 卡片任意位置都能拖。提起时轻轻放大，松手后滑回虚线框。
  function bindReorderDrag() {
    var dragCard = null;
    var placeholder = null;
    var pointerId = 0;
    var offsetX = 0;
    var offsetY = 0;
    var settling = false;
    var placeFrame = 0;

    function moveCard(x, y) {
      dragCard.style.left = (x - offsetX) + 'px';
      dragCard.style.top = (y - offsetY) + 'px';
    }

    function slotRect(node) {
      var transform = node.style.transform;
      var transition = node.style.transition;
      node.style.transition = 'none';
      node.style.transform = 'none';
      var rect = node.getBoundingClientRect();
      node.style.transform = transform;
      node.style.transition = transition;
      return rect;
    }

    function releaseDrag(card, slot) {
      var media = card.querySelector('.wishlist-card__media-wrap');
      if (slot && slot.parentElement) slot.parentElement.insertBefore(card, slot);
      if (slot) slot.remove();
      card.classList.remove('is-dragging');
      card.style.transition = '';
      card.style.transform = '';
      card.style.boxShadow = '';
      card.style.width = '';
      card.style.left = '';
      card.style.top = '';
      if (media) {
        media.style.transition = '';
        media.style.transform = '';
        media.style.boxShadow = '';
      }
      dragCard = null;
      placeholder = null;
      pointerId = 0;
      settling = false;
    }

    document.addEventListener('pointerdown', function (event) {
      if (!reorderMode || dragCard || settling) return;
      var card = event.target && event.target.closest && event.target.closest('.wishlist-card');
      if (!card || !card.parentElement || card.classList.contains('is-dragging')) return;
      event.preventDefault();
      var rect = card.getBoundingClientRect();
      var media = card.querySelector('.wishlist-card__media-wrap');
      var mediaRect = media ? media.getBoundingClientRect() : rect;
      var frame = document.createElement('div');
      frame.className = 'wishlist-card__placeholder-frame';
      frame.style.height = mediaRect.height + 'px';
      placeholder = document.createElement('div');
      placeholder.className = 'wishlist-card__placeholder';
      placeholder.style.height = rect.height + 'px';
      placeholder.appendChild(frame);
      card.parentElement.insertBefore(placeholder, card);
      var home = [];
      Array.prototype.forEach.call(card.parentElement.children, function (node) {
        if (node !== card) home.push(node);
      });
      placeholder._homeOrder = home;
      dragCard = card;
      pointerId = event.pointerId;
      offsetX = event.clientX - rect.left;
      offsetY = event.clientY - rect.top;
      try {
        if (card.setPointerCapture) card.setPointerCapture(event.pointerId);
      } catch (e) {}
      card.classList.add('is-dragging');
      card.style.width = rect.width + 'px';
      moveCard(event.clientX, event.clientY);
    });

    document.addEventListener('pointermove', function (event) {
      if (!dragCard || settling || event.pointerId !== pointerId) return;
      event.preventDefault();
      moveCard(event.clientX, event.clientY);
      if (placeFrame) return;
      placeFrame = requestAnimationFrame(function () {
        placeFrame = 0;
        if (!dragCard || settling || !placeholder) return;
        placeDraggedCard(placeholder, dragCard);
      });
    });

    function endDrag(event) {
      if (!dragCard || settling || (event && event.pointerId !== pointerId)) return;
      var card = dragCard;
      var slot = placeholder;
      if (!slot || !slot.parentElement) {
        releaseDrag(card, slot);
        return;
      }
      settling = true;
      var rect = slotRect(slot);
      var media = card.querySelector('.wishlist-card__media-wrap');
      var done = false;
      function finish(event) {
        if (done) return;
        if (event && event.propertyName && event.propertyName !== 'left' && event.propertyName !== 'top') return;
        done = true;
        card.removeEventListener('transitionend', finish);
        releaseDrag(card, slot);
      }
      card.addEventListener('transitionend', finish);
      card.style.transition = 'left ' + REORDER_MOVE + ', top ' + REORDER_MOVE + ', transform ' + REORDER_MOVE + ', box-shadow ' + REORDER_MOVE;
      card.style.left = rect.left + 'px';
      card.style.top = rect.top + 'px';
      if (media) {
        media.style.transition = 'transform ' + REORDER_MOVE + ', box-shadow ' + REORDER_MOVE;
        media.style.transform = 'scale(1)';
        media.style.boxShadow = 'none';
      }
      window.setTimeout(finish, 340);
    }

    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);

    // 排序中点图片、心形或文案只拖拽，不跳转、不取消收藏。
    document.addEventListener('click', function (event) {
      if (!reorderMode) return;
      var card = event.target && event.target.closest && event.target.closest('.wishlist-card');
      if (!card) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    document.addEventListener('dragstart', function (event) {
      if (!reorderMode) return;
      var card = event.target && event.target.closest && event.target.closest('.wishlist-card');
      if (card) event.preventDefault();
    });
  }

  // 收藏、切色、分享、排序和悬停提示都挂在 document 上，后插入的商品卡也能点。
  function bindEvents() {
    bindReorderDrag();

    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest && event.target.closest('[data-wishlist-toggle]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      toggle(button);
    }, true);

    // 商品卡手动切色：心形立刻改成这一色的收藏状态。
    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('[data-wishlist-color]')) return;
      syncButtonFromControl(input);
    }, true);

    // 色圈是链接而不是单选时，点一下就把这一色写成当前选中，心形跟着变。
    document.addEventListener('click', function (event) {
      var control = event.target && event.target.closest && event.target.closest('[data-wishlist-color]');
      if (!control || control.matches('input, select, option')) return;
      var group = control.parentElement;
      if (group) {
        Array.prototype.forEach.call(group.querySelectorAll('[data-wishlist-color]'), function (el) {
          el.classList.remove('js-is-active');
        });
      }
      control.classList.add('js-is-active');
      syncButtonFromControl(control);
    });

    // custom.js 比色选中颜色时不会触发 change，用这个事件跟上。
    document.addEventListener('wishlist:color', function (event) {
      var card = event.target && cardScope(event.target);
      if (!card) return;
      var button = card.querySelector('[data-wishlist-toggle]');
      if (button && button.getAttribute('data-wishlist-state') !== 'loading') paintButton(button);
    });

    document.addEventListener('variant:changed', onVariantChanged);
    document.addEventListener('variant:change', function (event) {
      var detail = event.detail || {};
      if (!detail.variant) return;
      var scope = detail.sectionId ? document.getElementById('shopify-section-' + detail.sectionId) : null;
      applyProductVariant(detail.variant, scope || document);
    });

    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          Array.prototype.forEach.call(mutation.addedNodes, function (node) {
            if (!node || node.nodeType !== 1) return;
            if (node.matches && node.matches('[data-wishlist-toggle]')) paintButton(node);
            else if (node.querySelectorAll) paintAll(node);
          });
        });
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    document.addEventListener('click', function (event) {
      var share = event.target && event.target.closest && event.target.closest('[data-wishlist-share]');
      if (share) {
        event.preventDefault();
        sharePage(share);
        return;
      }
      var reorder = event.target && event.target.closest && event.target.closest('[data-wishlist-reorder]');
      if (!reorder) return;
      event.preventDefault();
      reorderItems();
    });

    // 只给指针正下方的那颗心加提示。旁边已收藏的心形不再跟着亮。
    document.addEventListener('mouseover', function (event) {
      if (reorderMode || mobileLayout()) return;
      var button = event.target && event.target.closest && event.target.closest('.wishlist-button');
      if (!button || button.classList.contains('is-tip')) return;
      var open = document.querySelectorAll('.wishlist-button.is-tip');
      for (var i = 0; i < open.length; i++) open[i].classList.remove('is-tip');
      button.classList.add('is-tip');
    });

    document.addEventListener('mouseout', function (event) {
      var button = event.target && event.target.closest && event.target.closest('.wishlist-button');
      if (!button || !button.classList.contains('is-tip')) return;
      var next = event.relatedTarget;
      if (next && button.contains(next)) return;
      button.classList.remove('is-tip');
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (limitModal) hideLimitModal();
      else hideTip();
    });

    document.addEventListener('shopify:section:load', function () {
      loadSharedList().then(function () {
        syncMemberActions();
        paintAll();
        renderPage();
      });
    });
  }

  // 读本地、刷新心形和列表，再开始监听。有 proxy 时已登录和游客都会拉服务端。
  function boot() {
    loadInitial();
    syncMemberActions();
    paintAll();
    bindEvents();
    loadSharedList().then(function () {
      syncMemberActions();
      renderPage();
    });
    // 已经登录（含刚注册/登录后刷新回来）：先补上当时被拦下的那一条，再和服务端对账。
    if (config.customerId) {
      enqueue(applyPendingItem);
    }
    if (config.proxyPath && (config.customerId || ensureGuestSessionId())) {
      enqueue(pullAndMerge);
    }
  }

  // 给主题其它脚本用：读当前收藏，或在区块重载后重画。
  window.Wishlist = {
    getItems: function () { return items.slice(); },
    refresh: function () {
      paintAll();
      renderPage();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
